import { FC, useMemo } from 'react';
import { capitalize, Skeleton, Typography } from '@mui/material';
import {
    Category as CategoryIcon,
    Event as EventIcon,
    Gavel as GavelIcon,
    History as HistoryIcon,
    Image as ImageIcon,
    LocalAtm as LocalAtmIcon,
    MonetizationOn as MonetizationOnIcon,
    MonitorHeartOutlined as MonitorHeartOutlinedIcon,
} from '@mui/icons-material';
import moment from 'moment-timezone';

import ApiService from '../../../../../ApiService';
import OverviewList, { OverviewListItem } from '../../../../../components/List/OverviewList';
import Utils from '../../../../../components/Utils';

import { CampaignAdGroup } from '../../../../../types/Campaign';
import DspProductCategoryUtils from '../../../../../utils/DspProductCategoryUtils';
import { useQuery } from '@tanstack/react-query';
import { DspProductCategory } from '../../../../../types/DspProductCategory';

interface CampaignAdGroupOverviewListProps {
    campaignAdGroup: CampaignAdGroup;
}

const CampaignAdGroupOverviewList: FC<CampaignAdGroupOverviewListProps> = ({ campaignAdGroup }) => {
    const priceRangeParts = [
        campaignAdGroup.baseBid != null ? `Min: ${Utils.formatCurrency(campaignAdGroup.baseBid)}` : null,
        campaignAdGroup.maxBid != null ? `Max: ${Utils.formatCurrency(campaignAdGroup.maxBid)}` : null,
    ].filter(Boolean);

    const targetIds: string[] = DspProductCategoryUtils.toDspProductCategoryIds(
        campaignAdGroup?.adGroupProductCategories || []
    );

    const { data: dspProductCategories } = useQuery<DspProductCategory[]>({
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/dspProductCategories', campaignAdGroup.id, targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspProductCategories({
                ids: targetIds,
            });

            return data;
        },
    });

    const parentTargetIds = useMemo((): string[] => {
        return [...new Set(dspProductCategories?.map((target: DspProductCategory) => target.parentCategoryId) || [])];
    }, [dspProductCategories]);

    const { data: parentDspProductCategories, isLoading: parentDspProductCategoriesIsLoading } = useQuery<
        DspProductCategory[]
    >({
        enabled: !!parentTargetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/parentDspProductCategories', campaignAdGroup.id, targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspProductCategories({
                ids: parentTargetIds,
            });

            return data;
        },
    });

    const items: OverviewListItem[] = [
        {
            icon: CategoryIcon,
            primary: 'Product Category',
            secondary: parentDspProductCategoriesIsLoading ? (
                <Skeleton variant="text" width={120} sx={{ fontSize: '1rem' }} />
            ) : (
                <Typography variant="body2">
                    {parentDspProductCategories?.length
                        ? parentDspProductCategories?.map((target: DspProductCategory) => target.name)?.join(', ')
                        : 'Automatic'}
                </Typography>
            ),
        },
        {
            icon: ImageIcon,
            primary: 'Media Type',
            secondary: <Typography variant="body2">{campaignAdGroup?.mediaType || ' - '}</Typography>,
        },
        {
            icon: EventIcon,
            primary: 'Start Date',
            secondary: (
                <Typography variant="body2">
                    {campaignAdGroup?.startDateTime
                        ? moment(campaignAdGroup?.startDateTime).format('MMMM D, YYYY')
                        : ' - '}
                </Typography>
            ),
        },
        {
            icon: EventIcon,
            primary: 'End Date',
            secondary: (
                <Typography variant="body2">
                    {campaignAdGroup?.endDateTime ? moment(campaignAdGroup?.endDateTime).format('MMMM D, YYYY') : ' - '}
                </Typography>
            ),
        },
        {
            icon: MonetizationOnIcon,
            primary: 'Ad Group Budget',
            secondary: (
                <Typography variant="body2">
                    {campaignAdGroup?.automateBudgetAllocation ? 'Automatic' : 'Manual'},{' '}
                    {campaignAdGroup?.budgetAmount ? Utils.formatCurrency(campaignAdGroup?.budgetAmount) : ' - '}
                </Typography>
            ),
        },
        {
            icon: LocalAtmIcon,
            primary: 'Budget Cap',
            secondary: (
                <Typography variant="body2">
                    {capitalize((campaignAdGroup?.budgetCapType?.toLowerCase() as string) || ' - ')}
                </Typography>
            ),
        },
        {
            icon: HistoryIcon,
            primary: 'Budget Pacing',
            secondary: (
                <Typography variant="body2">
                    {capitalize((campaignAdGroup?.pacingDeliveryProfile?.toLowerCase() as string) || ' - ')}
                </Typography>
            ),
        },
        {
            icon: MonitorHeartOutlinedIcon,
            primary: 'Exposure Frequency',
            secondary: (
                <Typography variant="body2">
                    {capitalize(
                        ((campaignAdGroup.frequencyType === 'UNCAPPED'
                            ? 'Use default uncapped frequency'
                            : campaignAdGroup?.frequencyType?.toLocaleLowerCase()) as string) || ' - '
                    )}
                </Typography>
            ),
        },
        {
            icon: GavelIcon,
            primary: 'Bidding',
            secondary: (
                <Typography variant="body2">{priceRangeParts.length ? priceRangeParts.join(' – ') : ' - '}</Typography>
            ),
        },
    ];

    return <OverviewList items={items} sx={{ p: 0, width: '100%' }} />;
};

export type { CampaignAdGroupOverviewListProps };
export default CampaignAdGroupOverviewList;
